import { PlatformType } from './types/storyboard/storyboard-plan'

export const ConfigKey = {
  // Flagsmith uses snake_case
  /**
   * Enables event log compression during execution.
   */
  EXECUTION_EVENT_LOG_COMPRESSION_ENABLED: 'execution_event_log_compression_enabled',

  /**
   * Enables copy-on-write: the entity holds a single copy of its content
   * until modified. When modified, `prevContent` or `origContent` will be set.
   */
  ENTITY_COPY_ON_WRITE_ENABLED: 'entity_copy_on_write_enabled',

  /**
   * Enables three-way entity merge by comparing local changes with remote changes.
   */
  ENTITY_THREE_WAY_MERGE_ENABLED: 'entity_three_way_merge_enabled',

  /**
   * Enables merging newer server payloads into locally-dirty records instead of dropping them.
   */
  ENTITY_STALE_DIRTY_MERGE_ENABLED: 'entity_stale_dirty_merge_enabled',

  /**
   * Enable a read-only execution proxy to provide a unified view of the shared context and the execution content.
   */
  STORYBOARD_EXECUTION_SELF_PROXY_ENABLED: 'storyboard_execution_self_proxy_enabled',

  /**
   * Toggles LogRocket session replay to record and visualize user activity.
   */
  SESSION_REPLAY_ENABLED: "session_replay_enabled"
};

export type ConfigKeyType = keyof typeof ConfigKey
export type ConfigKeyValue = typeof ConfigKey[ConfigKeyType]

// Global lookup for default values of all configuration keys across platforms
export const DefaultConfig: Partial<Record<PlatformType, Partial<Record<string, any>>>> = {
  [PlatformType.MOBILE]: {
    [ConfigKey.STORYBOARD_EXECUTION_SELF_PROXY_ENABLED]: false,
    [ConfigKey.ENTITY_THREE_WAY_MERGE_ENABLED]: false,
    [ConfigKey.ENTITY_STALE_DIRTY_MERGE_ENABLED]: false,
    [ConfigKey.ENTITY_COPY_ON_WRITE_ENABLED]: false,
    [ConfigKey.EXECUTION_EVENT_LOG_COMPRESSION_ENABLED]: false,
    [ConfigKey.SESSION_REPLAY_ENABLED]: false,
  },
}

export function getDefaultConfig(
  platform: string,
  key: typeof ConfigKey[keyof typeof ConfigKey]
): any | undefined {
  return DefaultConfig[platform]?.[key] ?? false
}

export function getConfig(
  settings: {
    getPlatform?: () => string
    getConfigValue?: (key: string, defaultValue?: any) => any
  },
  key: typeof ConfigKey[keyof typeof ConfigKey],
  defaultValue: any = null
): any {
  const platform = settings?.getPlatform?.()
  const configValue = settings?.getConfigValue?.(key, defaultValue)

  // Try config from remote, then fallback to provided default, then system default
  return configValue ?? defaultValue ?? getDefaultConfig(platform, key)
}
